import { useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';

interface AnimatedButtonProps {
  children: ReactNode;
  onClick?: () => void;
  href?: string;
  to?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function AnimatedButton({
  children,
  onClick,
  href,
  to,
  variant = 'primary',
  size = 'md',
  showIcon = false,
  className,
  style,
}: AnimatedButtonProps) {
  const [isHovered, setIsHovered] = useState(false);

  const baseStyles = cn(
    'relative overflow-hidden font-geist-mono inline-flex items-center justify-center gap-2',
    'transition-all duration-300 ease-out-quad active:scale-[0.98] active:duration-75',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg)]',
    {
      'bg-text-primary text-surface-elevated hover:bg-text-primary/90': variant === 'primary',
      'bg-surface-elevated text-text-primary border border-exvia-border hover:border-text-primary': variant === 'secondary',
      'bg-transparent text-text-primary border border-text-primary hover:bg-text-primary hover:text-surface-elevated': variant === 'outline',
      'px-4 py-2 text-xs': size === 'sm',
      'px-6 py-3 text-sm': size === 'md',
      'px-8 py-4 text-base': size === 'lg',
    },
    className
  );

  // Hover animation slides the label up while a twin slides in from below.
  // When children is a string, the twin uses a CSS ::before pseudo fed by
  // `data-text` so the label only appears once in the DOM textContent
  // (screen readers and crawlers see "Let's Talk", not "Let's TalkLet's Talk").
  // For non-string children (icons etc.) we fall back to a hidden DOM twin
  // marked aria-hidden so a11y tools still hear the label only once.
  const childText = typeof children === 'string' ? children : null;
  const twinClass = cn(
    'absolute inset-0 flex items-center transition-transform duration-350 ease-out-quad',
    isHovered ? 'translate-y-0' : 'translate-y-[150%]'
  );

  const content = (
    <>
      <span className="relative overflow-hidden h-5 flex items-center">
        <span
          className={cn(
            'block transition-transform duration-350 ease-out-quad',
            isHovered ? '-translate-y-[150%]' : 'translate-y-0'
          )}
        >
          {children}
        </span>
        {childText !== null ? (
          <span
            aria-hidden="true"
            data-text={childText}
            className={cn(twinClass, "before:content-[attr(data-text)]")}
          />
        ) : (
          <span aria-hidden="true" className={twinClass}>
            {children}
          </span>
        )}
      </span>
      {showIcon && (
        <ArrowRight
          className={cn(
            'w-4 h-4 transition-transform duration-300 ease-out-quad',
            isHovered ? 'translate-x-1' : 'translate-x-0'
          )}
        />
      )}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        onClick={onClick}
        className={baseStyles}
        style={style}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={baseStyles}
        style={style}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      onClick={onClick}
      className={baseStyles}
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {content}
    </button>
  );
}
